/**
 * Where a fund's rounds happen, in regions finer than "Europe": the buckets a
 * founder's investor list is read by. One constant, like the sector tags, so
 * the written list Marjorie sends can replace it without a hunt.
 *
 * The first six are Europe, split the way an investor talks about it; the
 * rest are the places outside it that a European fund's portfolio turns up.
 * The order is the order a picker lists them.
 */
export const REGIONS = [
    'DACH',
    'UK & Ireland',
    'France & Benelux',
    'Nordics',
    'Southern Europe',
    'Eastern Europe',
    'North America',
    'Latin America',
    'Israel',
    'Middle East & Africa',
    'Asia-Pacific',
];
/** The first six: what "Europe" means as shorthand for a filter. */
export const EUROPEAN_REGIONS = REGIONS.slice(0, 6);
const COUNTRIES_BY_REGION = {
    DACH: 'DE AT CH LI',
    'UK & Ireland': 'GB IE GG JE IM',
    'France & Benelux': 'FR BE NL LU MC',
    Nordics: 'SE NO DK FI IS FO',
    'Southern Europe': 'ES PT IT GR MT CY AD SM VA GI',
    'Eastern Europe': 'PL CZ SK HU RO BG HR SI RS BA ME MK AL XK EE LV LT UA MD BY RU GE AM AZ',
    'North America': 'US CA',
    'Latin America': `MX GT BZ SV HN NI CR PA CU DO HT JM PR TT BS BB CO VE EC PE BO BR PY UY AR CL GY SR`,
    Israel: 'IL PS',
    'Middle East & Africa': `TR AE SA QA KW BH OM JO LB IQ IR SY YE EG LY TN DZ MA SD
    NG GH KE ET TZ UG RW SN CI CM ZA ZW ZM MZ NA BW MU MG AO CD`,
    'Asia-Pacific': `CN HK MO TW JP KR KP MN IN PK BD LK NP BT MV SG MY ID TH VN PH KH LA MM BN
    TL AU NZ FJ PG KZ UZ KG TJ TM AF`,
};
const REGION_BY_COUNTRY = new Map(REGIONS.flatMap((region) => COUNTRIES_BY_REGION[region]
    .trim()
    .split(/\s+/)
    .map((code) => [code, region])));
/** Every placed country with its region: for a query that has to place countries itself. */
export const COUNTRY_REGIONS = [...REGION_BY_COUNTRY];
/** The region an ISO 3166-1 alpha-2 code falls in, or null for a code the table does not place. */
export function regionOfCountry(code) {
    return REGION_BY_COUNTRY.get(code.trim().toUpperCase()) ?? null;
}
/**
 * Region names as a caller may spell them: the eleven, any case, and "Europe"
 * for the six European ones. A name nobody can read is returned in `unknown`
 * rather than dropped, so a typo does not silently widen the filter.
 */
export function resolveRegions(names) {
    const regions = new Set();
    const unknown = [];
    for (const name of names) {
        const wanted = name.trim().toLowerCase();
        if (wanted === 'europe') {
            for (const region of EUROPEAN_REGIONS)
                regions.add(region);
            continue;
        }
        const match = REGIONS.find((region) => region.toLowerCase() === wanted);
        if (match === undefined)
            unknown.push(name);
        else
            regions.add(match);
    }
    return { regions: [...regions], unknown };
}
/** A region is kept when it is this share of the fund's placed rounds... */
export const REGION_MIN_SHARE = 0.15;
/** ...or when it is at least this many of them, however small the share. */
export const REGION_MIN_DEALS = 2;
/**
 * Where a fund's rounds were, from one HQ country per distinct round (null when
 * the company has none). A round whose country is unknown or unplaced is not
 * evidence of any region and is left out of the shares too. Strongest first,
 * ties in the order of `REGIONS`. The list filter states the same rule in SQL.
 */
export function observedRegionsOf(countries) {
    const dealsByRegion = new Map();
    for (const country of countries) {
        const region = country === null ? null : regionOfCountry(country);
        if (region !== null)
            dealsByRegion.set(region, (dealsByRegion.get(region) ?? 0) + 1);
    }
    const placed = [...dealsByRegion.values()].reduce((sum, deals) => sum + deals, 0);
    return REGIONS.flatMap((region) => {
        const deals = dealsByRegion.get(region) ?? 0;
        const share = placed === 0 ? 0 : deals / placed;
        return deals > 0 && (share >= REGION_MIN_SHARE || deals >= REGION_MIN_DEALS)
            ? [{ region, share, deals }]
            : [];
    }).sort((a, b) => b.deals - a.deals);
}
//# sourceMappingURL=regions.js.map